import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  OnInit,
  ChangeDetectorRef
} from '@angular/core';

import { first } from 'rxjs/operators';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { FeedbackService } from 'app/shared/services/feedback.service';
import { ToastService } from 'app/shared/services/toast.service';

import { ChatFacade } from '../../store/chat';

import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';

import { IMessage } from 'app/interfaces/conversations.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import { ICreatorContent } from 'app/interfaces/creator.interfaces';
import { IGenerateImage } from 'app/shared/interfaces/media.interfaces';
import { IFeedbackPayload } from 'app/interfaces/feedback.intefaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { EFeedbackType } from 'app/enums/feedback.enums';
import { EToastPosition, EToastType } from 'app/shared/enums/toast.enums';
import { ETooltipPlacement } from 'app/shared/enums/shared.enums';

@Component({
  selector: 'stxt-feedback-controls',
  standalone: true,
  imports: [CommonModule, NgbTooltipModule, SvgIconComponent],
  templateUrl: './feedback-controls.component.html',
  styleUrl: './feedback-controls.component.scss'
})
export class FeedbackControlsComponent implements OnInit, OnChanges {
  @Input() message: IMessage;
  @Input() selectedMedia: IGenerateImage | ICreatorContent;
  @Input() creatorId: string;
  @Input() userId: string;
  conversationId: string;
  iconDisabled: boolean;

  private readonly baseFeedbackControls: ISvgConfig[] = [
    { fill: ESvgTypes.None, name: EFeedbackType.ThumbsUp, label: 'Good response' },
    { fill: ESvgTypes.None, name: EFeedbackType.ThumbsDown, label: 'Bad response' }

    // TODO: These are for the upcoming cycle, current cycle (51) will have only thumbs up and thumbs down
    // { fill: ESvgTypes.None, name: EFeedbackType.Report, label: 'Report' },
    // { fill: ESvgTypes.None, name: EFeedbackType.Regenerate, label: 'Regenerate' }
  ];

  public ETooltipPlacement = ETooltipPlacement;
  public feedbackControls: ISvgConfig[] = [];
  public hoveredIndex: number | null = null;
  public selectedIndex: number | null = null;

  private isMobile: boolean = false;
  private readonly fillableIcons = new Set([
    EFeedbackType.ThumbsUp,
    EFeedbackType.ThumbsDown,
    EFeedbackType.Report
  ]);

  constructor(
    private readonly feedbackService: FeedbackService,
    private readonly toastService: ToastService,
    private readonly modal: NgbModal,
    private readonly chatFacade: ChatFacade,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    this.conversationId = `${this.creatorId}-${this.userId}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedMedia'] || changes['message']) {
      this.updateFeedbackControls();
      this.initializeFeedbackState();
      this.iconDisabled = this.isIconDisabled();
    }
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 640;
  }

  private getToastPosition(): EToastPosition {
    return this.isMobile ? EToastPosition.TOP : EToastPosition.BOTTOM;
  }

  private updateFeedbackControls(): void {
    this.feedbackControls = this.selectedMedia
      ? [...this.baseFeedbackControls]
      : this.baseFeedbackControls.filter(control => control.name !== EFeedbackType.Regenerate);
  }

  private initializeFeedbackState(): void {
    if (this.message?.user_feedback && this.message.user_feedback.trim() !== '') {
      const feedbackIndex = this.feedbackControls.findIndex(
        control => control.name === this.message.user_feedback
      );
      this.selectedIndex = feedbackIndex !== -1 ? feedbackIndex : null;
    } else {
      this.selectedIndex = null;
    }
  }

  getIconConfig(control: ISvgConfig, index: number): ISvgConfig {
    return {
      ...control,
      name: this.getIconType(control, index)
    };
  }

  private getIconType(control: ISvgConfig, index: number): string {
    const base = control.name;
    const isSelected = this.selectedIndex === index;
    const isHovered = this.hoveredIndex === index;

    const shouldFill = this.iconDisabled ? isSelected : isSelected || isHovered;

    return shouldFill && this.fillableIcons.has(base as EFeedbackType) ? `${base}_filled` : base;
  }

  isIconDisabled(): boolean {
    return this.message?.user_feedback && this.message.user_feedback.trim() !== '';
  }

  onSelectFeedback(index: number, name: string): void {
    if (this.isIconDisabled()) return;
    this.selectedIndex = this.selectedIndex === index ? null : index;
    name === EFeedbackType.ThumbsUp
      ? this.handlePositiveFeedback()
      : this.handleNegativeFeedback(name);
  }

  private handlePositiveFeedback(): void {
    this.feedbackService
      .sendFeedback(this.creatorId, this.message.timestamp, {
        score: 1,
        reason: '',
        comment: ''
      })
      .pipe(first())
      .subscribe(next => {
        if (next) {
          const updatedMessage = { ...this.message, user_feedback: EFeedbackType.ThumbsUp };
          this.message = updatedMessage;
          this.chatFacade.updateMessageByTimestamp(
            this.conversationId,
            updatedMessage,
            Number(this.message.timestamp)
          );
          this.showFeedbackToast();
          this.cdr.detectChanges();
        }
      });
  }

  private handleNegativeFeedback(name: string): void {
    const modalRef = this.modal.open(FeedbackModalComponent);
    modalRef.componentInstance.message = this.message;
    modalRef.componentInstance.feedbackType = name;
    modalRef.componentInstance.feedbackTypeSvg = { fill: ESvgTypes.None, name: name };

    modalRef.closed.subscribe((feedback: IFeedbackPayload) => {
      if (!feedback) return;
      this.feedbackService
        .sendFeedback(this.creatorId, this.message.timestamp, feedback)
        .pipe(first())
        .subscribe(next => {
          if (!next) return;
          this.message.user_feedback = name;
          const updatedMessage = { ...this.message, user_feedback: name };
          this.chatFacade.updateMessageByTimestamp(
            this.conversationId,
            updatedMessage,
            Number(this.message.timestamp)
          );
          this.showFeedbackToast();
        });
    });
  }

  private showFeedbackToast(): void {
    this.toastService.showToast({
      toastHeading: 'Thanks for your feedback',
      toastType: EToastType.SUCCESS,
      position: this.getToastPosition()
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
  }
}
